<script setup lang="ts">
import {
  computed,
  ref,
  useAuth,
  useDialog,
  useI18n,
  useSupabaseClient,
  useToast
} from '#imports';
const supabase = useSupabaseClient();
const { t } = useI18n();
const { openDialog } = useDialog();

const { fetchMapBookmarks, fetchSpotBookmarks, currentUser } = useAuth();
const loading = ref(false);
const showTooltip = ref(false);
const toolTipContent = ref('')

interface BookmarkProps {
  bookmark: 'map_bookmarks' | 'spot_bookmarks' | 'post_bookmarks';
  version?: string;
  backgroundColor?: string;
  iconColor?: string;
  size?: 'small' | 'medium' | 'large';
  mapId?: string;
  spotId?: string;
  label?: string;
  link?: boolean;
  showLabel?: boolean;
  tooltip?: boolean;
}

const { showError } = useToast();

const props = withDefaults(defineProps<BookmarkProps>(), {
  bookmark: 'map_bookmarks',
  version: undefined,
  backgroundColor: undefined,
  iconColor: undefined,
  size: 'medium',
  mapId: undefined,
  spotId: undefined,
  label: undefined,
  link: false,
  showLabel: true,
  tooltip: false,
});

const idField = computed(() => {
  if (!props.bookmark) return '';
  switch (props.bookmark) {
    case 'map_bookmarks':
      return 'map_id';
    case 'spot_bookmarks':
      return 'spot_id';
    case 'post_bookmarks':
      return 'post_id';
    default:
      return '';
  }
});

const bookMarkData = computed(() => {
  if (
    !currentUser.value ||
    !currentUser.value?.profile ||
    !currentUser.value?.bookmarks
  ) {
    return false;
  }
  switch (props.bookmark) {
    case 'map_bookmarks':
      if (!currentUser.value.bookmarks.maps) return false;
      return currentUser.value.bookmarks.maps.find(
        (bookmark) => bookmark.map_id === props.mapId,
      );

    case 'spot_bookmarks':
      if (!currentUser.value.bookmarks.spots) return false;
      return currentUser.value.bookmarks.spots.find(
        (bookmark) => bookmark.spot_id === props.spotId,
      );
    case 'post_bookmarks':
      return false;
    default:
      return false;
  }
});

const handleBookmarkOperation = async (operation: 'insert' | 'delete') => {
  loading.value = true;
  if (!currentUser || !currentUser.value?.profile) {
    return;
  }

  const userId = currentUser.value?.profile.id;

  const payload =
    operation === 'insert'
      ? { user_id: userId, [idField.value]: props.mapId || props.spotId }
      : undefined;

  const query = supabase.from(props.bookmark)[operation](payload);

  if (operation === 'delete') {
    query.eq('user_id', userId).eq(idField.value, props.mapId || props.spotId);
  }

  const { error } = await query;

  if (error) {
    showError(
      t(
        `messages.bookmarks.error-${operation === 'insert' ? 'adding' : 'removing'}-bookmark`,
      ),
    );
  }
  if (props.bookmark === 'map_bookmarks') {
    await fetchMapBookmarks();
  } else if (props.bookmark === 'spot_bookmarks') {
    await fetchSpotBookmarks();
  }
  loading.value = false;
};

const addBookmark = async () => {
  await handleBookmarkOperation('insert');
  toolTipContent.value = "Saved to atlas"
  showTooltip.value = true;
  setTimeout(() => {
    showTooltip.value = false;
    toolTipContent.value = ""
  }, 2000);
};
const removeBookmark = () => handleBookmarkOperation('delete');


</script>
<template>
  <div v-if="props.bookmark === 'map_bookmarks' && props.mapId" :class="props.bookmark">
    <div v-if="currentUser && currentUser.bookmarks">
      <MPButton
        v-if="!bookMarkData && !loading"
        label="Save to Atlas"
        icon="ri-heart-add-line"
        version="secondary"
        :size="props.size"
        rounded
        @click="addBookmark"
      />
      <MPButton
        v-else-if="bookMarkData && !loading"
        :link="props.link"
        label="Saved to Atlas"
        icon="ri-heart-fill"
        icon-color="var(--color-rose-500)"
        version="secondary"
        :size="props.size"
        rounded
        @click="removeBookmark"
      />
      <MPButton
        v-if="loading"
        :link="props.link"
        :size="props.size"
        icon="ri-heart-add-line"
        version="secondary"
        rounded
      />
    </div>
    <MPButton
      v-else
      :link="props.link"
      :size="props.size"
      label="Save to Atlas"
      icon-color="var(--color-slate-800)"
      icon="ri-heart-add-line"
      version="secondary"
      rounded
      @click="openDialog('MPAuth', { startOn: 'login' })"
    />

</div>
<div
  v-else-if="props.bookmark === 'spot_bookmarks' && props.spotId"
  :class="props.bookmark"
>
    <div v-if="currentUser?.bookmarks">
      <MPButton
        v-if="!bookMarkData && !loading"
        :size="props.size"
        :link="props.link"
        icon="ri-heart-add-line"
        icon-color="white"
        label="add bookmark"
        hide-label
        @click="addBookmark"
      />
      <MPButton
        v-else-if="bookMarkData && !loading"
        :size="props.size"
        :link="props.link"
        icon="ri-heart-fill"
        icon-color="var(--color-rose-500)"
        label="remove bookmark"
        hide-label
        @click="removeBookmark"
      />
      <MPButton
        v-if="loading"
        :size="props.size"
        :link="props.link"
        icon="ri-loader-line"
        icon-color="white"
        label="Loading"
        hide-label
      />
    </div>
    <MPButton
      v-else
      :size="props.size"
      :link="props.link"
      icon="ri-heart-add-line"
      label="add bookmark"
      hide-label
      @click="openDialog('MPAuth', { startOn: 'login' })"
    />
    <MPTooltip
      v-if="props.tooltip"
      :content="toolTipContent"
      mode="fade"
      :show="showTooltip"
      class="tooltip-placement"
    />
  </div>
</template>
<style scoped>
@import "@/assets/css/typography.pcss";
.tooltip-placement {
  position: absolute;
  bottom: 110%;
  left: 50%;
  transform: translateX(-50%);
}

.map_bookmarks {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  @mixin typeface-regular;
}

.spot_bookmarks {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  @mixin typeface-regular;
}

.spot_bookmarks :deep(button) {
  border: none !important;
}

.spot_bookmarks :deep(i) {
  background-color: v-bind('props.backgroundColor || "var(--color-slate-300)"') !important;
  padding-block: 0.55rem;
  padding-inline: 0.60rem;
  border-radius: 100%;
  color: v-bind('props.iconColor || "var(--color-black)"')
}

</style>
